import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ModuleOptionsActions } from './module-options-actions.interface';
import { ProjectModule } from '../project-module.interface';
import { AuthService } from '../../../auth/auth.service';
import { ModuleService } from '../module.service';
import { ElementService } from '../../element/element.service';
import { SubmoduleService } from '../../submodule/submodule.service';
import { ScrollToService } from '../../../utilities/scroll-to/scroll-to.service';
import { TranslateService } from '../../../utilities/translate/translate.service';
import { ToastService } from '../../../services/toast/toast.service';
import { ConstantsStatusType } from '../../../utilities/constants/constants.interface';
import { ConstantsService } from '../../../utilities/constants/constants.service';
import { UserService } from '../../user/user.service';
import { User } from '../../user/user.interface';

@Component({
  selector: 'sg2-module-options',
  templateUrl: './module-options.component.html',
  styleUrls: ['./module-options.component.less']
})
export class ModuleOptionsComponent implements OnInit {

  /** Default options. */
  private readonly OPTIONS_DEFAULT: ModuleOptionsActions['action'][] = ['delete'];

  /** Disabled state of menu. */
  @Input() disabled: boolean;

  /** Module. */
  @Input() module: ProjectModule;

  /** Set custom selection of options. */
  @Input() options: string[];

  /** Dispatch action. Kind of @depricated with new datastore. */
  @Output() action = new EventEmitter<ModuleOptionsActions>();

  /** Constants status list. */
  public statuses: ConstantsStatusType[];

  /** Configuration. */
  public config: {
    addSubmodule?: boolean;
    assignTo?: boolean;
    delete?: boolean;
    setStatus?: boolean;
  } = {};


  constructor(
    public authService: AuthService,
    private constantsService: ConstantsService,
    private elementService: ElementService,
    private moduleService: ModuleService,
    private scrollToService: ScrollToService,
    private submoduleService: SubmoduleService,
    private toastService: ToastService,
    private translateService: TranslateService,
    public userService: UserService
  ) { }

  ngOnInit() {
    this.statuses = this.constantsService.getStatusTypes();
    this._setOptions();
  }


  /**
   * Add submodule.
   *
   * When adding a submodule, a new elements is also created and added to the new submodule.
   * After creation, scroll to the new submodule and make it flash for a second.
   *
   * @todo Toast should be added to SubmoduleService.add(), but that can cause problems due to
   *  Material Snackbar not supporting stacked toasts.
   *
   * @param module The parent module.
   */
  public addSubmodule(module: ProjectModule): void {
    this.submoduleService.add(module).subscribe(submodule => {
      this.elementService.addParagraph(submodule).subscribe(response => {
        this.toastService.open(this.translateService.get('submoduleAdded'));
        this.scrollToService.find(submodule._id, { animate: 'flash' });
      });
    });
  }

  /**
   * Assign all elements to user.
   *
   * @param module The parent module of the elements.
   * @param user The user to assign to each element.
   */
  public assignToUser(module: ProjectModule, user: User) {
    const activeElements = [].concat(...module.submodules.filter(s => s.active).map(s => s.elements.filter(e => e.active)));
    this.elementService.assignToBulk(activeElements, user, true);
  }

  /**
   * Delete module.
   *
   * @param module The module to delete.
   */
  public delete(module: ProjectModule): void {
    this.moduleService.delete(module, true);
  }

  /**
   * Set status of each element.
   *
   * @param module The parent module of the elements.
   * @param status The status to set on each element.
   */
  public setStatus(module: ProjectModule, status: ConstantsStatusType) {
    const activeElements = [].concat(...module.submodules.filter(s => s.active).map(s => s.elements.filter(e => e.active)));
    this.elementService.setStatusBulk(activeElements, status._id, true);
  }

  /**
   * Set menu options.
   *
   * A custom selection of options can be added by using the options binding.
   * When no options are given, the default selection is used.
   */
  private _setOptions(): void {
    this.options = this.options || this.OPTIONS_DEFAULT;
    this.options.forEach(o => {
      this.config[o] = true;
    });
  }

}
